import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import Seo from "@/components/feature/Seo";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import { articles, latestArticles } from "@/mocks/articles";
import AnalysisToolbar from "@/pages/analysis/components/AnalysisToolbar";

export default function AnalysisPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [activeCategory, setActiveCategory] = useState("all");
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const base = sort === "hot" ? [...articles].sort((a, b) => b.heat - a.heat) : latestArticles;
    return base.filter((item) => {
      const matchCategory =
        activeCategory === "all" || item.categorySlug === activeCategory;
      if (!matchCategory) return false;
      if (!keyword) return true;
      const haystack = [item.title, item.summary, item.category, ...item.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [query, activeCategory, sort]);

  const resetFilters = () => {
    setQuery("");
    setActiveCategory("all");
    setSort("latest");
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Seo
        title="深度解读库 - 事件复盘·内幕解析与真相挖掘 | 15黑料网"
        description="15黑料网深度解读库汇总全部事件复盘与内幕解析内容，支持按主题筛选与关键词搜索，每篇梳理时间线、关键角色与疑点清单。"
        keywords="事件解读库,事件复盘,内幕解析,真相挖掘,15黑料网"
        canonicalPath="/analysis"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "深度解读库",
            url: "https://sznita.cn/analysis",
            description: "汇总全部事件深度解读与内幕解析内容，支持按主题筛选与关键词搜索。",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://sznita.cn/" },
              { "@type": "ListItem", position: 2, name: "深度解读", item: "https://sznita.cn/analysis" },
            ],
          },
        ]}
      />
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1400px] px-4 pb-10 pt-8 md:px-8 md:pb-12 md:pt-10">
            <Breadcrumbs items={[{ label: "深度解读" }]} />
            <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              深度解读库
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里汇总了 15黑料网已完成的全部事件复盘。同一件事，我们尽量还原它的时间顺序、标出关键角色，并把还没
              有解释清楚的疑点单独列出来。你可以按主题筛选，也可以直接用关键词定位你想看的事件。
            </p>
          </div>
        </section>

        <AnalysisToolbar
          query={query}
          onQueryChange={setQuery}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          sort={sort}
          onSortChange={setSort}
          resultCount={filtered.length}
        />

        <section aria-label="解读列表" className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-12">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((article, index) => (
                  <Reveal key={article.id} delay={(index % 3) * 70}>
                    <ArticleCard article={article} variant="feature" />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-20 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的解读
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                  可能是关键词太具体了，试试只保留事件的核心词，或者切换一个主题再看。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}